// JSON code-editor key handling (Tab indent, Shift+Tab outdent, Enter keeps
// indentation) shared by ParameterValueField and OutputExampleField.
// Pure text transforms, no React/DOM — callers wire the keyboard events.

import { themeColor, color } from '../../../layout/theme/color'

export const MONO_FONT = '"SF Mono", "Fira Code", monospace'

export const monoInputSx = {
  'fontFamily': MONO_FONT,
  'fontSize': 12.5,
  'background': themeColor.slate05,
  'transition': 'background 120ms ease',
  '&.Mui-focused': { background: color.white },
  '& .MuiInputBase-inputMultiline': { resize: 'vertical' },
} as const

export const lineIndent = (text: string, caret: number): string => {
  const lineStart = text.lastIndexOf('\n', caret - 1) + 1
  const match = /^[ \t]*/.exec(text.slice(lineStart, caret))
  return match ? match[0] : ''
}

export interface EditorKeyEdit {
  text: string
  caret: number
}

// Returns the new text + caret, 'consume' to swallow the key without a
// change, or null to let the default behaviour proceed.
export const applyEditorKey = (
  text: string,
  key: string,
  shiftKey: boolean,
  start: number,
  end: number,
): EditorKeyEdit | 'consume' | null => {
  if (key === 'Tab' && !shiftKey) {
    return { text: `${text.slice(0, start)}  ${text.slice(end)}`, caret: start + 2 }
  }
  if (key === 'Tab' && shiftKey) {
    const lineStart = text.lastIndexOf('\n', start - 1) + 1
    const outdent = /^ {1,2}/.exec(text.slice(lineStart))
    if (!outdent) return 'consume'
    const removed = outdent[0].length
    return {
      text: text.slice(0, lineStart) + text.slice(lineStart + removed),
      caret: Math.max(lineStart, start - removed),
    }
  }
  if (key === 'Enter') {
    const indent = lineIndent(text, start)
    const before = text.slice(0, start).trimEnd()
    const opensBlock = before.endsWith('{') || before.endsWith('[')
    const nextChar = text.slice(end).trimStart().charAt(0)
    if (opensBlock && (nextChar === '}' || nextChar === ']')) {
      // {|} → opening line, indented caret line, closing line.
      const inner = `\n${indent}  `
      return {
        text: `${text.slice(0, start)}${inner}\n${indent}${text.slice(end)}`,
        caret: start + inner.length,
      }
    }
    const inner = `\n${indent}${opensBlock ? '  ' : ''}`
    return { text: `${text.slice(0, start)}${inner}${text.slice(end)}`, caret: start + inner.length }
  }
  return null
}
